import { cn } from '~/lib/cn'

/**
 * A run of choices as one control: a track holding equal shares of its width, the one in force
 * lit. It is for a short, fixed, exclusive set — three ways of saying what a nutrient figure
 * counts against — where a row of loose chips left the reader counting how far the choices
 * went and how far the form did.
 *
 * Filling the width is the point: the segments say how many there are by dividing the room
 * between them, so a choice is a place on the track rather than a pill that happens to be lit.
 */
export function Segmented<T extends string>({
  label,
  options,
  value,
  onValue,
  className,
}: {
  /** What the run of choices is asking, for ears — the legend says it for eyes. */
  label: string
  options: readonly { value: T; label: string }[]
  value: T
  onValue: (next: T) => void
  className?: string
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        'flex w-full gap-1 rounded-full bg-surface p-1 ring-1 ring-white/10',
        className,
      )}
    >
      {options.map((option) => {
        const on = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={on}
            className={`h-9 flex-1 rounded-full text-sm transition-colors ${
              on
                ? 'bg-accent/15 font-medium text-accent ring-1 ring-accent/40 ring-inset'
                : 'text-ink-muted hover:bg-hover hover:text-ink'
            }`}
            onClick={() => {
              onValue(option.value)
            }}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
